<template>
  <div
    class="rich-text-editor"
    :class="[
      `rich-text-editor--${variant}`,
      { 'rich-text-editor--focused': isFocused },
      { 'rich-text-editor--has-error': hasError }
    ]"
  >
    <!-- Custom Toolbar -->
    <transition name="toolbar-slide">
      <EditorToolbar
        v-if="shouldShowToolbar"
        :editor="trixEditor"
        :button-groups="variantConfig.toolbarButtons"
        :show-toolbar="true"
        :active-attributes="activeAttributes"
        @action="handleToolbarAction"
        @attribute-changed="updateActiveAttributes"
        @insert-embed="handleInsertEmbed"
      />
    </transition>

    <!-- Trix Editor -->
    <div class="rich-text-editor__container">
      <input
        :id="inputId"
        :name="inputName"
        type="hidden"
        :value="value"
      />
      <!-- `inputId` belongs to the hidden input above, so a consumer's
           <label for="..."> never reaches this element. Naming it explicitly is
           what stops screen readers announcing an unlabelled text box. -->
      <trix-editor
        ref="trixEditor"
        :input="inputId"
        :placeholder="computedPlaceholder"
        :aria-label="ariaLabel || computedPlaceholder"
        class="rich-text-editor__editor"
        :style="editorStyles"
        @trix-initialize="handleInitialize"
        @trix-change="handleChange"
        @trix-focus="handleFocus"
        @trix-blur="handleBlur"
        @trix-selection-change="updateActiveAttributes"
        @trix-file-accept="handleFileAccept"
        @trix-attachment-add="handleAttachmentAdd"
        @trix-attachment-remove="handleAttachmentRemove"
        @trix-paste="handlePaste"
      ></trix-editor>

      <!-- Layout toggle for image galleries (2+ adjacent images). Trix groups
           them automatically; this lets the author pick how the group renders.
           mousedown.prevent keeps the editor selection where it was. -->
      <div
        v-for="control in galleryControls"
        :key="control.key"
        class="rich-text-editor__gallery-toggle"
        :style="{ top: control.top + 'px', right: control.right + 'px' }"
      >
        <button
          type="button"
          class="rich-text-editor__gallery-btn"
          :class="{ 'rich-text-editor__gallery-btn--active': !control.isGrid }"
          :title="$t('Full width')"
          :aria-label="$t('Full width')"
          :aria-pressed="String(!control.isGrid)"
          @mousedown.prevent
          @click.prevent="setGalleryLayout(control, 'stack')"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
            <rect x="2" y="2.5" width="12" height="4.5" rx="1" />
            <rect x="2" y="9" width="12" height="4.5" rx="1" />
          </svg>
        </button>
        <button
          type="button"
          class="rich-text-editor__gallery-btn"
          :class="{ 'rich-text-editor__gallery-btn--active': control.isGrid }"
          :title="$t('Side by side')"
          :aria-label="$t('Side by side')"
          :aria-pressed="String(control.isGrid)"
          @mousedown.prevent
          @click.prevent="setGalleryLayout(control, 'grid')"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
            <rect x="2" y="2.5" width="5.5" height="11" rx="1" />
            <rect x="8.5" y="2.5" width="5.5" height="11" rx="1" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Draft Indicator (floating at bottom) -->
    <transition name="fade">
      <div v-if="hasDraftContent && showDraftIndicator" class="rich-text-editor__draft-indicator">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="rich-text-editor__draft-icon">
          <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
        </svg>
        <span>{{ $t('Draft restored') }}</span>
        <button type="button" class="rich-text-editor__draft-dismiss" @click="dismissDraftIndicator" :aria-label="$t('Dismiss')">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
          </svg>
        </button>
      </div>
    </transition>

    <!-- Upload Error -->
    <div v-if="uploadError" class="rich-text-editor__upload-error">
      <span class="rich-text-editor__error-icon">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-5 h-5">
          <path fill-rule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-8-5a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-1.5 0v-4.5A.75.75 0 0 1 10 5Zm0 10a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clip-rule="evenodd" />
        </svg>
      </span>
      <span>{{ uploadError }}</span>
      <button v-if="failedAttachment" type="button" class="rich-text-editor__retry-btn" @click="retryUpload">
        {{ $t('Retry') }}
      </button>
    </div>

    <!-- Upload Status -->
    <transition name="fade">
      <div
        v-if="activeUploads > 0 || showUploadSuccess"
        class="rich-text-editor__upload-status"
        :class="{ 'rich-text-editor__upload-status--success': showUploadSuccess && activeUploads === 0 }"
      >
        <span
          v-if="activeUploads > 0"
          class="rich-text-editor__upload-spinner"
          aria-hidden="true"
        ></span>
        <span
          v-else
          class="rich-text-editor__upload-check"
          aria-hidden="true"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M16.704 5.29a1 1 0 0 1 .006 1.414l-7.1 7.16a1 1 0 0 1-1.42 0L3.29 8.965a1 1 0 1 1 1.415-1.414l4.194 4.193 6.392-6.445a1 1 0 0 1 1.413-.008Z" clip-rule="evenodd" />
          </svg>
        </span>
        <span>{{ uploadStatusMessage }}</span>
      </div>
    </transition>

    <!-- Hidden file input for image uploads -->
    <input
      v-if="enableImageUpload"
      ref="fileInput"
      type="file"
      accept="image/jpeg,image/png,image/gif,image/webp"
      class="hidden"
      @change="handleFileSelect"
    />
  </div>
</template>

<script>
import Trix from 'trix';
import EditorToolbar from './EditorToolbar.vue';
import { getVariantConfig, IMAGE_CONFIG } from './editorConfig';
import { useMarkdownShortcuts } from './useMarkdownShortcuts';
import { useMentions, extractMentionStrings } from './useMentions';
import { useEmbeds } from './useEmbeds';
import { useDrafts } from './useDrafts';
import EventBus from 'event_bus';

export default {
  name: 'RichTextEditor',

  components: {
    EditorToolbar
  },

  props: {
    // Core
    value: {
      type: String,
      default: ''
    },
    inputId: {
      type: String,
      required: true
    },
    inputName: {
      type: String,
      required: true
    },
    // Accessible name for the editable region. Falls back to the placeholder,
    // which is descriptive enough to beat no name at all.
    ariaLabel: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },

    // Variant
    variant: {
      type: String,
      default: 'minimal',
      validator: v => ['full', 'minimal', 'comment', 'internal', 'description'].includes(v)
    },

    // Features
    enableMentions: {
      type: Boolean,
      default: false
    },
    mentionUsers: {
      type: Array,
      default: () => []
    },
    enableImageUpload: {
      type: Boolean,
      default: true
    },
    enableAttachments: {
      type: Boolean,
      default: true
    },
    enableEmbeds: {
      type: Boolean,
      default: true
    },
    enableDrafts: {
      type: Boolean,
      default: true
    },
    draftId: {
      type: String,
      default: ''
    },
    // Default must stay the endpoint that is open to everyone who can post:
    // /updates/images is team-only and 401s for customers (comments, widget,
    // the 5-minute own-post edit window).
    uploadUrl: {
      type: String,
      default: '/posts/images'
    },

    // AI (comments only)
    enableAiSuggestions: {
      type: Boolean,
      default: false
    },
    boardSlug: {
      type: String,
      default: ''
    },
    postId: {
      type: [String, Number],
      default: ''
    },

    // Error state
    hasError: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      trixEditor: null,
      isFocused: false,
      activeAttributes: {},
      uploadError: null,
      failedAttachment: null,
      activeUploads: 0,
      showUploadSuccess: false,
      uploadSuccessTimer: null,
      hasDraftContent: false,
      showDraftIndicator: false,
      isInternalChange: false, // Flag to prevent watcher loop
      galleryControls: [],
      galleryRefreshFrame: null,

      // Composable cleanup functions
      markdownCleanup: null,
      mentionsCleanup: null,
      embedsCleanup: null,
      draftsManager: null
    };
  },

  computed: {
    variantConfig() {
      return getVariantConfig(this.variant);
    },

    computedPlaceholder() {
      return this.placeholder || this.$t(this.variantConfig.placeholder);
    },

    editorStyles() {
      const styles = {
        minHeight: this.variantConfig.minHeight
      };

      if (this.variantConfig.autoExpand && this.isFocused) {
        styles.minHeight = this.variantConfig.expandedMinHeight;
      }

      return styles;
    },

    computedDraftId() {
      return this.draftId || `${this.variant}_${this.inputId}`;
    },

    shouldShowToolbar() {
      return this.variantConfig.showToolbar;
    },

    uploadStatusMessage() {
      if (this.activeUploads > 1) {
        return `Uploading ${this.activeUploads} images...`;
      }

      if (this.activeUploads === 1) {
        return 'Uploading image...';
      }

      return 'Image uploaded';
    }
  },

  watch: {
    value(newValue) {
      // Only sync external value changes to editor (not internal changes from typing)
      if (this.isInternalChange) {
        this.isInternalChange = false;
        return;
      }
      // Sync external value changes to editor
      if (this.trixEditor && newValue !== undefined) {
        const currentHTML = this.getSerializedHTML(this.$refs.trixEditor);
        if (currentHTML !== newValue) {
          this.trixEditor.loadHTML(newValue || '');
        }
      }
    },

    mentionUsers: {
      handler(users) {
        // Update mention users if mentions are enabled
        if (this.mentionsInstance) {
          this.mentionsInstance.tribute.collection[0].values = users;
        }
      },
      deep: true
    }
  },

  created() {
    this.clearDraftHandler = (payload = {}) => {
      this.handleClearDraftRequest(payload);
    };
    EventBus.$on('clearRichTextDraft', this.clearDraftHandler);
  },

  mounted() {
    // Initialize after trix-editor is in DOM
    this.$nextTick(() => {
      // The trix-initialize event will handle the rest
    });
  },

  beforeDestroy() {
    EventBus.$off('clearRichTextDraft', this.clearDraftHandler);
    this.cleanup();
  },

  methods: {
    getSerializedHTML(editorElement = this.$refs.trixEditor) {
      if (!editorElement) return '';

      // Trix stores normalized/portable HTML in the linked input value.
      if (typeof editorElement.value === 'string') {
        return editorElement.value;
      }

      return editorElement.innerHTML || '';
    },

    handleInitialize(event) {
      this.trixEditor = event.target.editor;
      const editorElement = this.$refs.trixEditor;

      // Load initial content
      if (this.value) {
        this.trixEditor.loadHTML(this.value);
      }

      // Initialize markdown shortcuts
      this.markdownCleanup = useMarkdownShortcuts(editorElement);

      // Initialize mentions if enabled
      if (this.enableMentions && this.mentionUsers.length > 0) {
        const mentionsResult = useMentions(editorElement, this.mentionUsers);
        this.mentionsInstance = mentionsResult;
        this.mentionsCleanup = mentionsResult.cleanup;
      }

      // Initialize embeds if enabled
      if (this.enableEmbeds && this.variantConfig.enableEmbeds) {
        const embedsResult = useEmbeds(editorElement, { enabled: true });
        this.embedsInstance = embedsResult;
        this.embedsCleanup = embedsResult.cleanup;
      }

      // Initialize drafts if enabled
      if (this.enableDrafts) {
        this.draftsManager = useDrafts(this.computedDraftId);

        // Load existing draft
        const draft = this.draftsManager.loadDraft();
        if (draft && !this.value) {
          this.trixEditor.loadHTML(draft);
          this.hasDraftContent = true;
          this.showDraftIndicator = true;

          // Hide indicator after 5 seconds
          setTimeout(() => {
            this.showDraftIndicator = false;
          }, 5000);
        }
      }

      // Update active attributes
      this.updateActiveAttributes();

      // Gallery layout toggles: refresh on anything that can move or resize
      // a gallery. Trix re-renders attachments without firing trix-change
      // (e.g. on setAttributes), so watch the DOM itself; images loading
      // async need the capture-phase load listener on top.
      this.galleryListenersTarget = editorElement;
      editorElement.addEventListener('load', this.scheduleGalleryControlRefresh, true);
      window.addEventListener('resize', this.scheduleGalleryControlRefresh);
      this.galleryObserver = new MutationObserver(this.scheduleGalleryControlRefresh);
      this.galleryObserver.observe(editorElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-trix-attachment', 'class']
      });
      this.scheduleGalleryControlRefresh();

      // Emit initialized event
      this.$emit('initialized', this.trixEditor);
    },

    handleChange(event) {
      const html = this.getSerializedHTML(event.target);
      // Set flag to prevent watcher from reloading HTML
      this.isInternalChange = true;
      this.$emit('input', html);
      this.$emit('change', html);

      // Save draft
      if (this.draftsManager) {
        this.draftsManager.saveDraftDebounced(html);
      }

      this.scheduleGalleryControlRefresh();
    },

    handleFocus(event) {
      this.isFocused = true;
      this.$emit('focus', event);
      this.refreshGalleryControlsAfterTransition();
    },

    handleBlur(event) {
      this.isFocused = false;
      this.$emit('blur', event);
      this.refreshGalleryControlsAfterTransition();
    },

    handlePaste(event) {
      // Check if this is a URL that should be embedded
      const paste = event.paste;
      if (paste) {
        const text = paste.string?.trim();

        // If it's a URL and embeds are enabled, check if it's embeddable
        if (text && this.embedsInstance) {
          const urlPattern = /^https?:\/\/[^\s]+$/;
          if (urlPattern.test(text)) {
            const detected = this.embedsInstance.detectProvider(text);
            if (detected) {
              // Let useEmbeds handle this - it will insert the embed
              event.preventDefault();
              this.embedsInstance.insertEmbed(text);
              return;
            }
          }
        }

        // Strip formatting - paste as plain text for non-embed content
        if (paste.type === 'text/html') {
          const plainText = paste.string;
          if (plainText) {
            event.preventDefault();
            this.trixEditor.insertString(plainText);
          }
        }
      }
    },

    // Trix accepts pasted and dropped files regardless of our props. When
    // inline images are off (comments, widget), hand the file to the paired
    // "Attach images" modal instead of dropping it: those bodies strip <img>
    // on save, and the widget's origin can't reach the inline upload route.
    handleFileAccept(event) {
      if (this.enableImageUpload) return;
      event.preventDefault();
      EventBus.$emit('richTextFileRedirect', { inputId: this.inputId, file: event.file });
    },

    handleAttachmentAdd(event) {
      const attachment = event.attachment;

      if (!attachment.file) return;

      const file = attachment.file;

      // Validate file
      if (!this.validateFile(file)) {
        attachment.remove();
        return;
      }

      // Upload file
      this.uploadFile(attachment);
    },

    handleAttachmentRemove(event) {
      const attachment = event.attachment;
      // Loading saved HTML can emit removal events as Trix replaces its
      // initial document. Only clean up files added in this editing session;
      // persisted board descriptions and notes may reference unattached blobs.
      if (!attachment.file) return;
      const blobId = attachment.attachment?.attributes?.values?.blob_id;

      if (blobId) {
        this.deleteFile(blobId);
      }
    },

    validateFile(file) {
      let error = null;
      if (file.size > IMAGE_CONFIG.maxSize) {
        error = this.$t('File size exceeds {max}', { max: IMAGE_CONFIG.maxSizeFormatted });
      } else if (IMAGE_CONFIG.blockedTypes.includes(file.type)) {
        error = this.$t('SVG files are not allowed');
      } else if (!IMAGE_CONFIG.allowedTypes.includes(file.type)) {
        error = this.$t('Invalid file type. Allowed: JPG, PNG, GIF, WebP');
      }

      this.uploadError = error;
      // A rejected pick replaces any earlier server failure in the banner, so
      // Retry must not resend that older attachment.
      if (error) this.failedAttachment = null;
      return !error;
    },

    uploadFile(attachment) {
      const file = attachment.file;
      const data = new FormData();
      data.append('image', file);
      this.startUploadIndicator();

      let uploadSucceeded = false;

      // Show progress
      attachment.setUploadProgress(0);

      this.uploadRequest('POST', this.uploadUrl, data)
        .then((response) => {
          uploadSucceeded = true;
          attachment.setUploadProgress(100);
          attachment.setAttributes({
            url: response.image_link,
            href: response.image_link,
            blob_id: response.image_id
          });
          this.uploadError = null;
          this.failedAttachment = null;
        })
        .catch(() => {
          this.uploadError = this.$t('Upload failed. Please try again.');
          this.failedAttachment = attachment;

          // Keep attachment but mark as error
          attachment.setAttributes({
            previewable: false
          });
        })
        .finally(() => {
          this.finishUploadIndicator(uploadSucceeded);
        });
    },

    deleteFile(blobId) {
      this.uploadRequest('DELETE', `${this.uploadUrl}/${blobId}`).catch(() => {
        console.error('Failed to delete file');
      });
    },

    // fetch, not Rails.ajax: the widget and intercom layouts load only the
    // webpack packs, so the rails-ujs global from the asset pipeline is
    // undefined there. Rejects on any non-2xx status.
    uploadRequest(method, url, body) {
      const csrfToken = document.querySelector('meta[name="csrf-token"]');
      return fetch(url, {
        method,
        body,
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken ? csrfToken.getAttribute('content') : ''
        }
      }).then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        // Delete endpoints return a plain OK body; only uploads return JSON.
        return method === 'DELETE' ? null : res.json();
      });
    },

    retryUpload() {
      if (this.failedAttachment && this.failedAttachment.file) {
        this.uploadError = null;
        this.uploadFile(this.failedAttachment);
      }
    },

    handleToolbarAction(action) {
      if (action === 'insertImage') {
        this.openFilePicker();
      }
    },

    handleInsertEmbed(url) {
      if (this.embedsInstance) {
        this.embedsInstance.insertEmbed(url);
      }
    },

    openFilePicker() {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.click();
      }
    },

    handleFileSelect(event) {
      const file = event.target.files[0];
      if (!file) return;

      if (!this.validateFile(file)) {
        return;
      }

      // Insert file using Trix's built-in method which properly
      // sets the file property on the attachment for upload
      this.trixEditor.insertFile(file);

      // Clear file input
      event.target.value = '';
    },

    updateActiveAttributes() {
      if (!this.trixEditor) return;

      const attributes = this.trixEditor.composition.currentAttributes;
      this.activeAttributes = { ...attributes };
    },

    scheduleGalleryControlRefresh() {
      if (this.galleryRefreshFrame) cancelAnimationFrame(this.galleryRefreshFrame);
      this.galleryRefreshFrame = requestAnimationFrame(() => {
        this.galleryRefreshFrame = null;
        this.refreshGalleryControls();
      });
    },

    // The editor min-height animates on focus/blur for auto-expand variants,
    // so one immediate refresh would measure mid-transition positions.
    refreshGalleryControlsAfterTransition() {
      this.scheduleGalleryControlRefresh();
      setTimeout(() => this.scheduleGalleryControlRefresh(), 300);
    },

    refreshGalleryControls() {
      const editorEl = this.$refs.trixEditor;
      const containerEl = editorEl && editorEl.closest('.rich-text-editor__container');
      if (!editorEl || !containerEl || !this.trixEditor) {
        this.galleryControls = [];
        return;
      }

      const containerRect = containerEl.getBoundingClientRect();
      const controls = [];

      editorEl.querySelectorAll('div.attachment-gallery').forEach((gallery, index) => {
        const figures = [...gallery.querySelectorAll('figure.attachment')];
        if (figures.length < 2) return;

        const rect = gallery.getBoundingClientRect();
        const attachmentIds = figures
          .map(figure => Number(figure.getAttribute('data-trix-id')))
          .filter(id => !Number.isNaN(id));

        controls.push({
          key: attachmentIds.join('-') || `gallery-${index}`,
          top: rect.top - containerRect.top + 8,
          right: containerRect.right - rect.right + 8,
          isGrid: figures.some(figure =>
            (figure.getAttribute('data-trix-attachment') || '').includes('"gallery_layout":"grid"')
          ),
          attachmentIds
        });
      });

      this.galleryControls = controls;
    },

    setGalleryLayout(control, layout) {
      if (!this.trixEditor) return;

      this.trixEditor
        .getDocument()
        .getAttachments()
        .filter(attachment => control.attachmentIds.includes(attachment.id))
        .forEach(attachment => attachment.setAttributes({ gallery_layout: layout }));

      this.scheduleGalleryControlRefresh();
    },

    clearDraftAndContent() {
      this.clearDraft();
      this.trixEditor.loadHTML('');
      this.hasDraftContent = false;
      this.showDraftIndicator = false;
      this.$emit('input', '');
    },

    clearDraft() {
      if (this.draftsManager) {
        this.draftsManager.clearDraft();
      }
      this.hasDraftContent = false;
      this.showDraftIndicator = false;
    },

    dismissDraftIndicator() {
      this.showDraftIndicator = false;
    },

    // Public methods
    focus() {
      if (this.$refs.trixEditor) {
        this.$refs.trixEditor.focus();
      }
    },

    blur() {
      if (this.$refs.trixEditor) {
        this.$refs.trixEditor.blur();
      }
    },

    getContent() {
      if (this.trixEditor) {
        return this.trixEditor.getDocument().toString();
      }
      return '';
    },

    getHTML() {
      return this.getSerializedHTML(this.$refs.trixEditor);
    },

    getMentions() {
      if (this.$refs.trixEditor) {
        return extractMentionStrings(this.$refs.trixEditor);
      }
      return [];
    },

    clearContent() {
      if (this.trixEditor) {
        this.trixEditor.loadHTML('');
      }
      this.clearDraft();
      this.$emit('input', '');
    },

    insertText(text) {
      if (this.trixEditor) {
        this.trixEditor.insertString(text);
      }
    },

    cleanup() {
      if (this.uploadSuccessTimer) {
        clearTimeout(this.uploadSuccessTimer);
      }
      if (this.galleryRefreshFrame) {
        cancelAnimationFrame(this.galleryRefreshFrame);
        this.galleryRefreshFrame = null;
      }
      if (this.galleryListenersTarget) {
        this.galleryListenersTarget.removeEventListener('load', this.scheduleGalleryControlRefresh, true);
        this.galleryListenersTarget = null;
      }
      if (this.galleryObserver) {
        this.galleryObserver.disconnect();
        this.galleryObserver = null;
      }
      window.removeEventListener('resize', this.scheduleGalleryControlRefresh);
      if (this.activeUploads > 0) {
        this.activeUploads = 0;
        this.emitUploadState();
      }
      if (this.markdownCleanup) {
        this.markdownCleanup();
      }
      if (this.mentionsCleanup) {
        this.mentionsCleanup();
      }
      if (this.embedsCleanup) {
        this.embedsCleanup();
      }
      if (this.draftsManager) {
        this.draftsManager.cleanup();
      }
    },

    startUploadIndicator() {
      if (this.uploadSuccessTimer) {
        clearTimeout(this.uploadSuccessTimer);
        this.uploadSuccessTimer = null;
      }

      this.showUploadSuccess = false;
      this.activeUploads += 1;
      this.emitUploadState();
    },

    finishUploadIndicator(uploadSucceeded) {
      this.activeUploads = Math.max(0, this.activeUploads - 1);
      this.emitUploadState();

      if (!uploadSucceeded) {
        this.showUploadSuccess = false;
        return;
      }

      if (this.activeUploads === 0) {
        this.showUploadSuccess = true;
        this.uploadSuccessTimer = setTimeout(() => {
          this.showUploadSuccess = false;
          this.uploadSuccessTimer = null;
        }, 1800);
      }
    },

    handleClearDraftRequest(payload = {}) {
      if (!this.enableDrafts) return;

      const requestedInputId = payload.inputId;
      const requestedDraftId = payload.draftId;
      const currentDraftId = this.computedDraftId;

      const matchesInputId = requestedInputId && requestedInputId === this.inputId;
      const matchesDraftId = requestedDraftId && requestedDraftId === currentDraftId;

      if (!matchesInputId && !matchesDraftId) return;

      this.clearDraft();
    },

    emitUploadState() {
      EventBus.$emit('richTextUploadStateChanged', {
        inputId: this.inputId,
        activeUploads: this.activeUploads,
        isUploading: this.activeUploads > 0
      });
    }
  }
};
</script>

<style scoped>
.hidden {
  display: none;
}
</style>
